import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORY_LABELS = {
  crisis: "Crisis & immediate help",
  support_group: "Support groups",
  online: "Online community",
  therapy: "Find a therapist",
  kids: "Children & teens",
};

export default function GriefSupport() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familyName, setFamilyName] = useState("");
  const [deceasedName, setDeceasedName] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [replyMsg, setReplyMsg] = useState("");
  const [replyOpts, setReplyOpts] = useState([]);
  const [replyTone, setReplyTone] = useState("gracious");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/grief/resources").then(({ data }) => setResources(data.resources || [])).finally(() => setLoading(false));
  }, []);

  const grouped = resources.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  const downloadAftercare = async () => {
    if (!familyName.trim()) return toast.error("Please enter the family name.");
    setDownloading(true);
    try {
      const res = await api.post("/grief/aftercare-checklist", { family_name: familyName.trim(), deceased_name: deceasedName.trim() }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aftercare-checklist-${familyName.trim().replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Checklist downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate checklist.");
    } finally { setDownloading(false); }
  };

  const generateReplies = async () => {
    if (!replyMsg.trim()) return toast.error("Paste the condolence message first.");
    setGenerating(true);
    setReplyOpts([]);
    try {
      const { data } = await api.post("/grief/sympathy-reply", { received_message: replyMsg, tone: replyTone });
      setReplyOpts(data.options || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate replies.");
    } finally { setGenerating(false); }
  };

  return (
    <div style={{ maxWidth: 920, margin: "40px auto", padding: "0 24px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 8 }}>YOU ARE NOT ALONE</div>
        <h1 className="serif" style={{ fontSize: 42, color: "#fff", margin: 0, fontWeight: 400 }}>Grief Support &amp; Aftercare</h1>
        <p className="muted" style={{ marginTop: 12, lineHeight: 1.7, maxWidth: 640 }}>
          Resources, a practical aftercare checklist, and tools to help you respond to condolences while you grieve.
        </p>
      </div>

      {/* ── Aftercare PDF ── */}
      <div className="bg-card" style={{ padding: 26, marginBottom: 24 }} data-testid="aftercare-section">
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 12 }}>AFTERCARE CHECKLIST</div>
        <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>A practical guide to the year that follows</div>
        <p className="muted" style={{ fontSize: 14, marginBottom: 18, lineHeight: 1.7 }}>
          Death certificates, SSA notifications, insurance claims, probate, taxes — a personalized PDF checklist with deadlines for every step.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <input data-testid="aftercare-family" value={familyName} onChange={e => setFamilyName(e.target.value)} placeholder="Family name (e.g. The Smiths)" style={{ padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          <input data-testid="aftercare-deceased" value={deceasedName} onChange={e => setDeceasedName(e.target.value)} placeholder="In remembrance of (optional)" style={{ padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          <button data-testid="aftercare-download" onClick={downloadAftercare} disabled={downloading} className="btn-gold">{downloading ? "Building…" : "Download PDF"}</button>
        </div>
      </div>

      {/* ── Sympathy reply composer ── */}
      <div className="bg-card" style={{ padding: 26, marginBottom: 24 }} data-testid="sympathy-section">
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 12 }}>SYMPATHY REPLY HELPER</div>
        <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>Gracious replies when words are hard</div>
        <p className="muted" style={{ fontSize: 14, marginBottom: 14 }}>Paste a condolence message you received — get three thoughtful reply options.</p>
        <textarea data-testid="sympathy-input" value={replyMsg} onChange={e => setReplyMsg(e.target.value)} rows={4} placeholder="I'm so sorry for your loss. Your father was a remarkable man…" style={{ width: "100%", padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8, resize: "vertical", marginBottom: 12 }} />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <select data-testid="sympathy-tone" value={replyTone} onChange={e => setReplyTone(e.target.value)} style={{ padding: 10, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }}>
            <option value="gracious">Gracious</option>
            <option value="brief">Brief</option>
            <option value="warm">Deeply warm</option>
            <option value="faith-filled">Faith-filled</option>
          </select>
          <button data-testid="sympathy-generate" onClick={generateReplies} disabled={generating} className="btn-gold">{generating ? "Composing…" : "Compose 3 replies"}</button>
        </div>
        {replyOpts.length > 0 && (
          <div style={{ display: "grid", gap: 10 }}>
            {replyOpts.map((opt, i) => (
              <div key={i} className="bg-card" style={{ padding: 16, background: "#161617" }}>
                <div className="muted" style={{ fontSize: 11, letterSpacing: "0.2em", marginBottom: 6 }}>OPTION {i + 1}</div>
                <div style={{ color: "#e6e6e6", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{opt}</div>
                <button onClick={() => { navigator.clipboard.writeText(opt); toast.success("Copied."); }} className="btn-ghost" style={{ marginTop: 10, fontSize: 12, padding: "6px 12px" }}>Copy</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Resources ── */}
      {loading ? <div className="shimmer" style={{ height: 200, borderRadius: 14 }} /> : (
        <div data-testid="resources-list">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 12 }}>{(CATEGORY_LABELS[cat] || cat).toUpperCase()}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
                {items.map(r => (
                  <a key={r.url} href={r.url} target="_blank" rel="noreferrer" className="bg-card" style={{ padding: 16, textDecoration: "none", color: "inherit" }}>
                    <div style={{ color: "#fff", fontSize: 15, marginBottom: 6 }}>{r.name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{r.contact}</div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
